import "dayjs/locale/id";
import dayjs, { Dayjs, ManipulateType } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

type DateType = Date | string | Dayjs | null;

export const isAfter = (date: DateType) => {
  let convertDate = dayjs(date);
  return dayjs().isAfter(convertDate);
};

export const isBefore = (date: DateType) => {
  let convertDate = dayjs(date);
  return dayjs().isBefore(convertDate);
};

export const datePeriod = (startDate: DateType, endDate: DateType, unit: ManipulateType) => {
  return dayjs(endDate).diff(startDate, unit) || 0; //0 value to prevent NaN return
};

export const getDateBetween = (
  startDate: DateType = "",
  endDate: DateType = "",
  unit: ManipulateType = "days",
  format: string = "DD-MM-YYYY"
) => {
  const period = datePeriod(startDate, endDate, unit);
  const dateArray = [];

  if (period > 0) {
    for (let i = 0; i <= period; i++) {
      dateArray.push(dayjs(startDate).add(i, unit).format(format));
    }
  }

  return dateArray;
};

// Input : (Date 1, Date 2)
// Output: String, eg. "3 hari", "3 bulan"
export const getDurationText = (start: DateType, end: DateType, course_type: string) => {
  if (course_type === "unlimited" && !end) return "14 hari";
  const totalMonths = datePeriod(start, end, "months");
  const totalDays = datePeriod(start, end, "days");

  if (totalMonths > 0) return `${totalMonths} bulan`;

  return `${totalDays} hari`;
};

export const getCountdown = (deadline: DateType) => {
  let diff = dayjs(deadline).diff(dayjs());

  if (isAfter(deadline)) {
    return "00 : 00 : 00";
  }

  const duration = dayjs.duration(diff);
  const hours = String(duration.hours()).padStart(2, "0");
  const minutes = String(duration.minutes()).padStart(2, "0");
  const seconds = String(duration.seconds()).padStart(2, "0");

  return `${hours} : ${minutes} : ${seconds}`;
};

export const isLessThanOneHourAgo = (date: DateType) => {
  const currentDate = dayjs();

  return currentDate.diff(dayjs(date), "hours") < 1;
};

export const roundUp = (date: DateType, roundBy: ManipulateType = "hour") => {
  const dayjsObj = dayjs(date);

  return dayjsObj.add(1, roundBy).startOf(roundBy);
};

export const timeTillNow = (date: DateType, unit: ManipulateType = "hours") => {
  return dayjs(date).diff(dayjs(), unit);
};

export const getPostDate = (date: DateType = "", lang?: string) => {
  if (!date) return null;

  if (lang) dayjs.locale(lang);

  return dayjs(date).fromNow();
};

export const getTimeFormatWithTimezone = (date: DateType) => {
  const time = dayjs.tz(date, "utc");
  const localtz = dayjs.tz.guess();
  const cloneDate = time.clone().tz(localtz);

  return dayjs(cloneDate);
};

export const getRegistrationQuota = (startDate: DateType, endDate: DateType) => {
  if (!startDate || !endDate) return 3;

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const diff = end.diff(start, "days");

  if (diff > 28) return 20;
  if (diff > 14) return 10;
  if (diff > 7) return 5;
  return 3;
};

export const getEarliestExpiredDate = (promoCodes: any) => {
  return promoCodes?.reduce(
    (earliest: any, item: any) => {
      const currentDate = dayjs();
      const expiredDate = dayjs(item?.expired_at);

      if (!expiredDate.isValid()) return { ...earliest, isContainUnlimitedVoucher: true };
      if (currentDate.isAfter(expiredDate)) return earliest; // skip expired date
      const diff = expiredDate.diff(currentDate);
      return diff < earliest?.diff ? { ...earliest, item, diff } : earliest;
    },
    { item: null, diff: Infinity, isContainUnlimitedVoucher: false }
  );
};

export const getCurrentDiffTimeInDays = (end: DateType) => {
  if (!end) return 0;
  const currentDate = dayjs();
  const endDate = dayjs.tz(end, "YYYY-MM-DDTHH:mm:ss", "UTC");
  const diff = endDate.diff(currentDate, "days", true);

  return Math.ceil(diff);
};

type GetTimeStamp = {
  date: DateType;
  getHourDiff?: (hour: number, minute: number) => string;
  getMonthDiff?: (date: DateType) => string;
};

export const getTimestamp = ({ date, getHourDiff, getMonthDiff }: GetTimeStamp) => {
  const today = dayjs();
  const completed = dayjs(date);

  const secondDiff = today.diff(completed, "seconds");
  const minuteDiff = today.diff(completed, "minutes");
  const hourDiff = today.diff(completed, "hours");
  const dayDiff = today.diff(completed, "days");
  const monthDiff = today.diff(completed, "months");

  if (secondDiff < 60) {
    return "Baru saja";
  }

  if (minuteDiff < 60) {
    return `${minuteDiff} menit yang lalu`;
  }

  if (hourDiff < 24) {
    const minute = minuteDiff - hourDiff * 60;
    return getHourDiff ? getHourDiff(hourDiff, minute) : `${hourDiff} jam yang lalu`;
  }

  if (dayDiff < 30) {
    return `${dayDiff} hari yang lalu`;
  }

  return getMonthDiff ? getMonthDiff(date) : `${monthDiff} bulan yang lalu`;
};
